import { base_url } from "../Api/configs";

const defaultHeaders = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

/**
 * Middleware to parse stringified response bodies
 * Some API responses return body as stringified JSON, this automatically parses it
 *
 * Handles these cases:
 * 1. { statusCode: 200, body: "{\"message\":\"...\"}" } -> parses body to object
 * 2. { statusCode: 200, body: "\"Plain text message\"" } -> parses to plain string
 * 3. { statusCode: 200, body: { message: "..." } } -> leaves as is
 * 4. { id: "..." } -> leaves as is (no body field)
 *
 * IMPORTANT: After calling any API, you can use response.body directly.
 * DO NOT manually parse with JSON.parse(data.body) - it's already done!
 *
 * Example (CORRECT):
 *   const response = await someAPI();
 *   const message = response.body.message; // ✓ Already parsed
 *   const message = response.body; // ✓ For plain strings
 *
 * Example (INCORRECT - will cause errors):
 *   const response = await someAPI();
 *   const parsed = JSON.parse(response.body); // ✗ Double parsing!
 */
export const parseResponseBody = (data: any) => {
  // Check if response has a body field and it's a string
  if (data && typeof data.body === "string") {
    try {
      // Try to parse the stringified body
      data.body = JSON.parse(data.body);

      // Check if it's still a string and looks like JSON (doubly-stringified)
      // Only try to parse again if it starts with { or [ (JSON objects/arrays)
      if (
        typeof data.body === "string" &&
        (data.body.trim().startsWith("{") ||
          data.body.trim().startsWith("["))
      ) {
        try {
          data.body = JSON.parse(data.body);
        } catch (innerError) {
          // If second parse fails, keep the first parse result
        }
      }
    } catch (error) {
      // If parsing fails, leave it as is (might not be JSON)
      console.warn("Failed to parse response body:", error);
    }
  }
  return data;
};

/**
 * Safe helper to get response body - handles both parsed and unparsed responses
 * Use this if migrating old code that manually parses
 *
 * @param data - API response
 * @returns Parsed body (will never throw, returns original if can't parse)
 */
export const getResponseBody = (data: any) => {
  if (!data) return data;

  // If body is already parsed (object/array), return it
  if (typeof data.body !== "string") {
    return data.body;
  }

  // If body is a string, try to parse it
  try {
    const parsed = JSON.parse(data.body);
    // If still a string after parsing, try once more
    if (typeof parsed === "string" &&
        (parsed.trim().startsWith("{") || parsed.trim().startsWith("["))) {
      try {
        return JSON.parse(parsed);
      } catch {
        return parsed;
      }
    }
    return parsed;
  } catch {
    // If parsing fails, return the string as-is
    return data.body;
  }
};

const apiClient = async (endpoint: string, options: RequestInit) => {
  try {
    const response = await fetch(`${base_url}${endpoint}`, {
      ...options,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Something went wrong!");
    }

    // Apply middleware to parse stringified body if present
    return parseResponseBody(data);
  } catch (error) {
    console.error("API Error:", error.message);
    throw error;
  }
};

export default apiClient;
